import { useId } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { messageForError } from "../i18n/errors";
import { useLocale, useT } from "../i18n/locale";
import { useLeadView } from "../leads/lead-queries";
import { opportunityScopeOf, useAccess } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { useLeadOpportunity } from "./opportunity-queries";
import { boxStepText, isClosedStage, stageLabel } from "./opportunity-texts";
import "./opportunities.css";

// Il riquadro «Opportunità» nella colonna destra della scheda (piano M6, R8, Task 25; LEAD-2; mock-up `scheda-lead.html`; UX_SYSTEM,
// «Modello globale della colonna destra»; SEZ-9, SEZ-10), compatto: l'Opportunità di adesso del Lead (l'aperta, oppure l'ultima chiusa)
// con «Fase» e «Prossimo passo», che segue disco e demo («Disco da fare», poi «Disco fatta, demo da prenotare», …: un esito di un
// appuntamento la fa rileggere, `appointments-queries.ts`), senza, «Nessuna aperta»; poi «Apri l'opportunità», sempre, che apre la
// finestra «Opportunità di …» come «Opportunità» in testata (`OpportunityDialog.tsx`: lì «Venduto», «Persa», «Riapri» e il perché di
// chi può soltanto vedere, una volta sola), e la nota sullo status del Lead, che non cambia mai. Senza la lettura dell'area
// `opportunities` il riquadro non c'è e non si legge nulla; un'Opportunità aperta fuori dal perimetro di chi guarda (la segue un collega
// e chi guarda vede soltanto le sue) la dice «Disco e demo», e qui il perché (`useLeadOpportunity`).

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  timeZone: string;
  /** «Apri l'opportunità»: la finestra della scheda. */
  onOpen: () => void;
};

export function OpportunityBox({ tab, card, timeZone, onOpen }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const view = useLeadView();
  const titleId = useId();
  // Chi non vede nemmeno le sue Opportunità non ha il riquadro (e gli appuntamenti dicono la fase, `AppointmentsSection`).
  const shown = opportunityScopeOf(access, "view") !== "none";
  const current = useLeadOpportunity(tab, card.lead.id, view, shown);
  if (!shown) return null;

  let body;
  if (current.status === "loading") {
    body = (
      <p className="g-opportunity-box__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (current.status === "error") {
    body = (
      <div className="g-opportunity-box__actions">
        <Reason tone="warning" role="alert">
          {messageForError(t, current.error)}
        </Reason>
        <Button onClick={current.retry}>{t("common.retry")}</Button>
      </div>
    );
  } else if (current.status === "notVisible") {
    body = <Reason>{t("opportunities.box.notVisible")}</Reason>;
  } else if (current.opportunity === null) {
    body = (
      <dl>
        <div>
          <dt>{t("opportunities.box.stage")}</dt>
          <dd>{t("opportunities.box.noneOpen")}</dd>
        </div>
      </dl>
    );
  } else {
    const { opportunity } = current;
    const reschedule = opportunity.nextStep === "reschedule" && !isClosedStage(opportunity.stage);
    body = (
      <dl>
        <div>
          <dt>{t("opportunities.box.stage")}</dt>
          <dd>{stageLabel(t, opportunity.stage)}</dd>
        </div>
        <div>
          <dt>{t("opportunities.box.next")}</dt>
          <dd data-reschedule={reschedule ? "true" : undefined}>{boxStepText(t, locale, timeZone, opportunity)}</dd>
        </div>
      </dl>
    );
  }

  return (
    <section className="g-lead-summary__section g-opportunity-box" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("opportunities.box.title")}</h3>
      {body}
      <div className="g-opportunity-box__actions">
        <Button variant="ghost" aria-haspopup="dialog" onClick={onOpen}>
          <Icon name="briefcase" />
          {t("opportunities.box.open")}
        </Button>
      </div>
      <p className="g-opportunity-box__note">{t("opportunities.box.note")}</p>
    </section>
  );
}
